/**
 * INSTAPLAY LATAM - SISTEMA DE ALERTAS Y CONFIRMACIONES RETRO CYBERPUNK
 * Reemplazo 100% temático y estilizado para window.alert() y window.confirm()
 */

// =========================================================================
// SISTEMA DE AUDIO RETRO (WEBAUDIO TOLERANTE A FALLOS Y SIN MEMORY LEAKS)
// =========================================================================
let sharedAudioContext = null;
let isAudioGloballyDisabled = false;
let userHasInteractedWithPage = false;

// Registrar interacción de usuario para cumplir con las políticas de autoplay
if (typeof window !== 'undefined') {
  const markUserInteraction = () => {
    userHasInteractedWithPage = true;
    window.removeEventListener('pointerdown', markUserInteraction);
    window.removeEventListener('keydown', markUserInteraction);
    window.removeEventListener('click', markUserInteraction);
  };
  window.addEventListener('pointerdown', markUserInteraction, { once: true, passive: true });
  window.addEventListener('keydown', markUserInteraction, { once: true, passive: true });
  window.addEventListener('click', markUserInteraction, { once: true, passive: true });
}

function getSafeAudioContext() {
  if (isAudioGloballyDisabled || typeof window === 'undefined') return null;

  // No inicializar audio si el usuario aún no interactuó (evita error del renderer en Chrome/Edge)
  if (!userHasInteractedWithPage && (typeof navigator === 'undefined' || !navigator.userActivation?.hasBeenActive)) {
    return null;
  }

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  try {
    if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
      sharedAudioContext = new AudioContextClass();
      
      // Manejar cambios de estado y dispositivos desconectados
      if (sharedAudioContext) {
        sharedAudioContext.onstatechange = () => {
          if (sharedAudioContext && (sharedAudioContext.state === 'interrupted' || sharedAudioContext.state === 'closed')) {
            isAudioGloballyDisabled = true;
          }
        };
      }
    }

    if (sharedAudioContext && sharedAudioContext.state === 'suspended') {
      sharedAudioContext.resume().catch(() => {
        // Silencioso si el SO no permite reanudar el audio
      });
    }

    return sharedAudioContext;
  } catch (_) {
    // Si el dispositivo de hardware no está disponible (ej: sin salida de audio o error WASAPI)
    isAudioGloballyDisabled = true;
    return null;
  }
}

// Sonidos sintéticos retro con Web Audio API (Cero dependencias)
function playRetroTone(type = 'success') {
  if (isAudioGloballyDisabled) return;

  try {
    const ctx = getSafeAudioContext();
    if (!ctx) return;

    if (type === 'success') {
      // Arpegio retro ascendente (C5 -> E5 -> G5)
      [523.25, 659.25, 783.99].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.08);
        gain.gain.setValueAtTime(0.08, ctx.currentTime + i * 0.08);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.08 + 0.15);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.08);
        osc.stop(ctx.currentTime + i * 0.08 + 0.15);
      });
    } else if (type === 'error') {
      // Tono de error descendente / buzz
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(220, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(110, ctx.currentTime + 0.25);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.25);
    } else if (type === 'warning') {
      // Tono de atención doble
      [440, 440].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.12);
        gain.gain.setValueAtTime(0.06, ctx.currentTime + i * 0.12);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.09);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.12);
        osc.stop(ctx.currentTime + i * 0.12 + 0.09);
      });
    }
  } catch {
    // Si el navegador bloquea audio o no interactuó aún, omitir sonido silenciosamente
    isAudioGloballyDisabled = true;
  }
}

// Inyector del Modal si no existe en el DOM
function ensureModalDOM() {
  let overlay = document.getElementById('retro-dialog-overlay');
  if (overlay) return overlay;

  overlay = document.createElement('div');
  overlay.id = 'retro-dialog-overlay';
  overlay.className = 'fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-sm transition-opacity duration-200 opacity-0 pointer-events-none overflow-y-auto';
  overlay.innerHTML = `
    <div id="retro-dialog-window" class="win-window max-w-md w-full my-auto max-h-[92vh] flex flex-col border-2 transform scale-95 transition-all duration-200">
      <!-- Titlebar -->
      <div id="retro-dialog-titlebar" class="px-3 py-1.5 flex-shrink-0 flex items-center justify-between font-mono font-bold text-sm select-none border-b-2">
        <div class="flex items-center gap-2 truncate pr-2">
          <span id="retro-dialog-title-icon" class="text-base flex-shrink-0">⚡</span>
          <span id="retro-dialog-title" class="pixel-font text-lg tracking-wider truncate">NOTIFICACIÓN</span>
        </div>
        <button type="button" id="retro-dialog-close-x" class="px-2 py-0.5 bg-black text-white hover:bg-red-600 border font-mono text-xs font-bold transition-all cursor-pointer flex-shrink-0">✕</button>
      </div>
      <!-- Body -->
      <div class="p-4 sm:p-5 bg-[#010903] space-y-4 overflow-y-auto overscroll-contain flex-1">
        <div class="flex items-start gap-4">
          <div id="retro-dialog-icon-box" class="text-3xl shrink-0 p-2.5 bg-[#021806] border rounded-sm flex items-center justify-center select-none">
            🟢
          </div>
          <div class="space-y-1.5 flex-1 min-w-0">
            <div id="retro-dialog-badge" class="text-[11px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 inline-block border">
              [ ESTADO: 200 OK ]
            </div>
            <div id="retro-dialog-text" class="text-sm font-mono text-white leading-relaxed whitespace-pre-line break-words">
            </div>
          </div>
        </div>
        <!-- Action Buttons -->
        <div id="retro-dialog-footer" class="pt-3 flex items-center justify-end gap-2 border-t">
          <button type="button" id="retro-dialog-btn-cancel" class="hidden px-4 py-2 font-mono text-xs font-bold transition-all cursor-pointer border">
            CANCELAR
          </button>
          <button type="button" id="retro-dialog-btn-ok" class="px-6 py-2 font-mono text-xs font-bold transition-all cursor-pointer border-2">
            [ ACEPTAR ]
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  return overlay;
}

/**
 * Muestra una alerta modal retro personalizada
 * @param {string} message - Mensaje a mostrar
 * @param {object} options - { title, type ('success'|'error'|'warning'|'info'), confirmText }
 * @returns {Promise<boolean>}
 */
export function showRetroAlert(message, options = {}) {
  const {
    title = null,
    type = 'success',
    confirmText = '[ ACEPTAR ]',
    badgeText = null
  } = options;

  return new Promise((resolve) => {
    const overlay = ensureModalDOM();
    const win = document.getElementById('retro-dialog-window');
    const titlebar = document.getElementById('retro-dialog-titlebar');
    const titleEl = document.getElementById('retro-dialog-title');
    const titleIconEl = document.getElementById('retro-dialog-title-icon');
    const iconBox = document.getElementById('retro-dialog-icon-box');
    const badgeEl = document.getElementById('retro-dialog-badge');
    const textEl = document.getElementById('retro-dialog-text');
    const btnCancel = document.getElementById('retro-dialog-btn-cancel');
    const btnOk = document.getElementById('retro-dialog-btn-ok');
    const closeX = document.getElementById('retro-dialog-close-x');

    // Configuración según tipo
    if (type === 'error') {
      titlebar.className = 'px-3 py-1.5 flex-shrink-0 flex items-center justify-between font-mono font-bold text-sm select-none border-b-2 bg-gradient-to-r from-red-600 to-rose-700 text-white border-red-800 gap-2 min-w-0';
      win.className = 'win-window max-w-md w-full my-auto max-h-[92vh] flex flex-col border-2 border-red-500 shadow-[0_0_25px_rgba(239,68,68,0.4)] transform scale-100 transition-all duration-200';
      titleEl.textContent = title || 'ERROR DEL SISTEMA // KERNEL';
      titleIconEl.textContent = '⛔';
      iconBox.textContent = '🔴';
      iconBox.className = 'text-3xl shrink-0 p-2.5 bg-[#1a0404] border border-red-500/50 rounded-sm flex items-center justify-center select-none';
      badgeEl.className = 'text-[11px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 inline-block border border-red-500 text-red-400 bg-[#250606]';
      badgeEl.textContent = badgeText || '[ ERROR CRÍTICO ]';
      btnOk.className = 'px-6 py-2 bg-red-950 hover:bg-red-600 hover:text-white text-red-300 border-2 border-red-500 font-mono text-xs font-bold transition-all cursor-pointer shadow-[0_0_12px_rgba(239,68,68,0.4)]';
      btnOk.textContent = confirmText;
    } else if (type === 'warning') {
      titlebar.className = 'px-3 py-1.5 flex-shrink-0 flex items-center justify-between font-mono font-bold text-sm select-none border-b-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black border-yellow-600 gap-2 min-w-0';
      win.className = 'win-window max-w-md w-full my-auto max-h-[92vh] flex flex-col border-2 border-yellow-400 shadow-[0_0_25px_rgba(250,204,21,0.4)] transform scale-100 transition-all duration-200';
      titleEl.textContent = title || 'ADVERTENCIA DEL SISTEMA';
      titleIconEl.textContent = '⚠️';
      iconBox.textContent = '⚠️';
      iconBox.className = 'text-3xl shrink-0 p-2.5 bg-[#191402] border border-yellow-500/50 rounded-sm flex items-center justify-center select-none';
      badgeEl.className = 'text-[11px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 inline-block border border-yellow-400 text-yellow-300 bg-[#241d03]';
      badgeEl.textContent = badgeText || '[ ATENCIÓN REQUERIDA ]';
      btnOk.className = 'px-6 py-2 bg-amber-950 hover:bg-yellow-400 hover:text-black text-yellow-300 border-2 border-yellow-400 font-mono text-xs font-bold transition-all cursor-pointer shadow-[0_0_12px_rgba(250,204,21,0.4)]';
      btnOk.textContent = confirmText;
    } else if (type === 'info') {
      titlebar.className = 'px-3 py-1.5 flex-shrink-0 flex items-center justify-between font-mono font-bold text-sm select-none border-b-2 bg-gradient-to-r from-cyan-400 to-blue-500 text-black border-cyan-600 gap-2 min-w-0';
      win.className = 'win-window max-w-md w-full my-auto max-h-[92vh] flex flex-col border-2 border-cyan-400 shadow-[0_0_25px_rgba(34,211,238,0.4)] transform scale-100 transition-all duration-200';
      titleEl.textContent = title || 'INFORMACIÓN DEL KERNEL';
      titleIconEl.textContent = 'ℹ️';
      iconBox.textContent = 'ℹ️';
      iconBox.className = 'text-3xl shrink-0 p-2.5 bg-[#031520] border border-cyan-500/50 rounded-sm flex items-center justify-center select-none';
      badgeEl.className = 'text-[11px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 inline-block border border-cyan-400 text-cyan-300 bg-[#041d2d]';
      badgeEl.textContent = badgeText || '[ KERNEL INFO ]';
      btnOk.className = 'px-6 py-2 bg-cyan-950 hover:bg-cyan-400 hover:text-black text-cyan-300 border-2 border-cyan-400 font-mono text-xs font-bold transition-all cursor-pointer shadow-[0_0_12px_rgba(34,211,238,0.4)]';
      btnOk.textContent = confirmText;
    } else {
      // Default: Success / Operación Exitosa
      titlebar.className = 'px-3 py-1.5 flex-shrink-0 flex items-center justify-between font-mono font-bold text-sm select-none border-b-2 bg-gradient-to-r from-[#00ff41] to-[#00aa2a] text-black border-[#00ff41] gap-2 min-w-0';
      win.className = 'win-window max-w-md w-full my-auto max-h-[92vh] flex flex-col border-2 border-[#00ff41] shadow-[0_0_25px_rgba(0,255,65,0.4)] transform scale-100 transition-all duration-200';
      titleEl.textContent = title || 'OPERACIÓN EXITOSA // KERNEL SYSADMIN';
      titleIconEl.textContent = '⚡';
      iconBox.textContent = '🟢';
      iconBox.className = 'text-3xl shrink-0 p-2.5 bg-[#021806] border border-[#00ff41]/50 rounded-sm flex items-center justify-center select-none';
      badgeEl.className = 'text-[11px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 inline-block border border-[#00ff41] text-[#39ff14] bg-[#032008]';
      badgeEl.textContent = badgeText || '[ ESTADO: 200 COMPLETADO ]';
      btnOk.className = 'px-6 py-2 bg-[#003300] hover:bg-[#00ff41] hover:text-black text-[#00ff41] border-2 border-[#00ff41] font-mono text-xs font-bold transition-all cursor-pointer shadow-[0_0_12px_rgba(0,255,65,0.4)]';
      btnOk.textContent = confirmText;
    }

    textEl.textContent = message;
    btnCancel.classList.add('hidden');

    playRetroTone(type);

    // Mostrar
    document.body.classList.add('overflow-hidden');
    overlay.classList.remove('pointer-events-none', 'opacity-0');
    overlay.classList.add('opacity-100');
    btnOk.focus();

    function cleanup() {
      document.body.classList.remove('overflow-hidden');
      overlay.classList.remove('opacity-100');
      overlay.classList.add('opacity-0', 'pointer-events-none');
      btnOk.removeEventListener('click', onOk);
      closeX.removeEventListener('click', onOk);
      overlay.removeEventListener('click', onBackdrop);
      document.removeEventListener('keydown', onKey);
    }

    function onOk() {
      cleanup();
      resolve(true);
    }

    function onBackdrop(e) {
      if (e.target === overlay) {
        onOk();
      }
    }

    function onKey(e) {
      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        onOk();
      }
    }

    btnOk.addEventListener('click', onOk);
    closeX.addEventListener('click', onOk);
    overlay.addEventListener('click', onBackdrop);
    document.addEventListener('keydown', onKey);
  });
}

/**
 * Muestra una confirmación modal retro personalizada (Sí / Cancelar)
 * @param {string} message - Mensaje o pregunta a confirmar
 * @param {object} options - { title, confirmText, cancelText, type }
 * @returns {Promise<boolean>}
 */
export function showRetroConfirm(message, options = {}) {
  const {
    title = 'CONFIRMACIÓN DEL SISTEMA',
    type = 'warning',
    confirmText = 'SÍ, CONFIRMAR',
    cancelText = 'CANCELAR',
    badgeText = '[ ACCIÓN REQUERIDA ]'
  } = options;

  return new Promise((resolve) => {
    const overlay = ensureModalDOM();
    const win = document.getElementById('retro-dialog-window');
    const titlebar = document.getElementById('retro-dialog-titlebar');
    const titleEl = document.getElementById('retro-dialog-title');
    const titleIconEl = document.getElementById('retro-dialog-title-icon');
    const iconBox = document.getElementById('retro-dialog-icon-box');
    const badgeEl = document.getElementById('retro-dialog-badge');
    const textEl = document.getElementById('retro-dialog-text');
    const btnCancel = document.getElementById('retro-dialog-btn-cancel');
    const btnOk = document.getElementById('retro-dialog-btn-ok');
    const closeX = document.getElementById('retro-dialog-close-x');

    titlebar.className = 'px-3 py-1.5 flex-shrink-0 flex items-center justify-between font-mono font-bold text-sm select-none border-b-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black border-yellow-600 gap-2 min-w-0';
    win.className = 'win-window max-w-md w-full my-auto max-h-[92vh] flex flex-col border-2 border-yellow-400 shadow-[0_0_25px_rgba(250,204,21,0.4)] transform scale-100 transition-all duration-200';
    titleEl.textContent = title;
    titleIconEl.textContent = '⚠️';
    iconBox.textContent = '❓';
    iconBox.className = 'text-3xl shrink-0 p-2.5 bg-[#191402] border border-yellow-500/50 rounded-sm flex items-center justify-center select-none';
    badgeEl.className = 'text-[11px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 inline-block border border-yellow-400 text-yellow-300 bg-[#241d03]';
    badgeEl.textContent = badgeText;
    textEl.textContent = message;

    btnCancel.className = 'px-4 py-2 bg-[#140808] hover:bg-red-950 text-red-400 hover:text-white border border-red-500 font-mono text-xs font-bold transition-all cursor-pointer';
    btnCancel.textContent = cancelText;
    btnCancel.classList.remove('hidden');

    btnOk.className = 'px-6 py-2 bg-amber-950 hover:bg-yellow-400 hover:text-black text-yellow-300 border-2 border-yellow-400 font-mono text-xs font-bold transition-all cursor-pointer shadow-[0_0_12px_rgba(250,204,21,0.4)]';
    btnOk.textContent = confirmText;

    playRetroTone(type);

    document.body.classList.add('overflow-hidden');
    overlay.classList.remove('pointer-events-none', 'opacity-0');
    overlay.classList.add('opacity-100');
    btnOk.focus();

    function cleanup() {
      document.body.classList.remove('overflow-hidden');
      overlay.classList.remove('opacity-100');
      overlay.classList.add('opacity-0', 'pointer-events-none');
      btnOk.removeEventListener('click', onConfirm);
      btnCancel.removeEventListener('click', onCancel);
      closeX.removeEventListener('click', onCancel);
      overlay.removeEventListener('click', onBackdrop);
      document.removeEventListener('keydown', onKey);
    }

    function onConfirm() {
      cleanup();
      resolve(true);
    }

    function onCancel() {
      cleanup();
      resolve(false);
    }

    function onBackdrop(e) {
      if (e.target === overlay) {
        onCancel();
      }
    }

    function onKey(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }

    btnOk.addEventListener('click', onConfirm);
    btnCancel.addEventListener('click', onCancel);
    closeX.addEventListener('click', onCancel);
    overlay.addEventListener('click', onBackdrop);
    document.addEventListener('keydown', onKey);
  });
}

/**
 * Toast flotante retro no bloqueante
 * @param {string} message - Texto del toast
 * @param {string} type - 'success' | 'error' | 'warning' | 'info'
 * @param {number} duration - Duración en ms
 */
export function showToast(message, type = 'success', duration = 3500) {
  let container = document.getElementById('retro-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'retro-toast-container';
    container.className = 'fixed bottom-4 left-3 right-3 sm:left-auto sm:right-5 sm:w-auto sm:max-w-sm z-[99999] flex flex-col gap-2 pointer-events-none select-none';
    document.body.appendChild(container);
  }

  const borderColors = {
    success: 'border-[#00ff41] text-[#39ff14]',
    error: 'border-red-500 text-red-400',
    warning: 'border-yellow-400 text-yellow-300',
    info: 'border-cyan-400 text-cyan-300'
  };
  const icons = {
    success: '🟢',
    error: '🔴',
    warning: '⚠️',
    info: 'ℹ️'
  };

  const toast = document.createElement('div');
  toast.className = `pointer-events-auto bg-[#020b04]/95 border-2 ${borderColors[type] || borderColors.success} p-2.5 sm:p-3 font-mono text-xs shadow-[0_0_15px_rgba(0,0,0,0.8)] backdrop-blur-sm transition-all duration-300 opacity-0 transform translate-y-2 flex items-center gap-2.5 max-w-full break-words`;
  toast.innerHTML = `
    <span class="text-base shrink-0 select-none">${icons[type] || '⚡'}</span>
    <span class="flex-1 text-white leading-tight break-words">${message}</span>
  `;

  container.appendChild(toast);
  playRetroTone(type);

  // Animación de entrada
  requestAnimationFrame(() => {
    toast.classList.remove('opacity-0', 'translate-y-2');
    toast.classList.add('opacity-100', 'translate-y-0');
  });

  setTimeout(() => {
    toast.classList.remove('opacity-100', 'translate-y-0');
    toast.classList.add('opacity-0', 'translate-y-2');
    setTimeout(() => {
      toast.remove();
      if (container.children.length === 0) {
        container.remove();
      }
    }, 300);
  }, duration);
}

// Opcional: Hacerlos accesibles globalmente en window por comodidad
if (typeof window !== 'undefined') {
  window.retroAlert = showRetroAlert;
  window.retroConfirm = showRetroConfirm;
  window.retroToast = showToast;
}
